import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  memo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { equipmentISOStatus, getEquipmentStatusColorByStatus, getEquipmentStatusColorByStatusOrCommunicationIssue, ROLE } from 'data/data';
import { useEnumApi } from 'contexts/EnumApiContext';
import { useNavigate } from 'react-router-dom';
import styles from "./svg.module.css";
import useConfig from 'hooks/useConfig';
import DashboardEquipmentDurationGraphs from 'components/inner/chart/DashboardEquipmentDurationGraphs';

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

const EMPTY_CARD = {
  id: 0,
  name: '-',
  status: '-',
  statusTitle: '-',
  equipOpStatusTitle: '-',
  equipmentNo: '-',
  equipmentCtgr: { name: '-' },
  imgFiles: '-',
  isoGrade: { code: '-' },
  equipOpStatus: 'STOP',
  hasCommunicationIssue : '-',
  temperature: '-',
  humidity: '-',
  co2: '-',
};

function RightAsideCard({ equipments, latestLogs, selectedId = null, autoRoll = true }) {
  const isViewCommError = process.env.REACT_APP_SHOW_NETWORK_ERROR_TO_ALL_YN === 'Y';
  const { getEnumValueByEnumNameAndCode, role } = useEnumApi();
  const { t } = useTranslation();
  const { mode } = useConfig();
  const navigate = useNavigate();

  /* 1️⃣ cards = derived data */
  const cards = useMemo(() => {
    if (!Array.isArray(equipments) || equipments.length === 0) {
      return [EMPTY_CARD];
    }

    const LOG_GROUP =
      latestLogs?.reduce((acc, log) => {
        acc[log.equipmentId] ??= {};
        acc[log.equipmentId][log.logCode] = log;
        return acc;
      }, {}) || {};

    return equipments.map((equip) => {
      const {
        id,
        name,
        status,
        modelName,
        equipmentNo,
        equipmentCtgr,
        imgFiles,
        lowestDiagnosisRank,
        latestEquipmentOp,
        hasCommunicationIssue
      } = equip;

      const temperatureLog = LOG_GROUP[id]?.T;
      const humidityLog = LOG_GROUP[id]?.RH;
      const co2Log = LOG_GROUP[id]?.CO2;

      return {
        id,
        name,
        status,
        statusTitle:
          getEnumValueByEnumNameAndCode(
            "EquipmentFaultStatus",
            status
          )?.[0]?.title || "-",
        equipOpStatusTitle:
          latestEquipmentOp?.opStatus === 'ACTIVE'
            ? t('message.common.text.active')
            : t('message.common.text.stop'),
        equipmentNo,
        equipmentCtgr,
        modelName,
        imgFiles,
        lowestDiagnosisRank,
        isoGrade:
          equipmentISOStatus.find(
            (iso) => iso.code === lowestDiagnosisRank
          ) || { code: "-" },
        equipOpStatus: latestEquipmentOp?.opStatus || 'STOP',
        hasCommunicationIssue,
        temperature: (process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' && temperatureLog)
          ? `${temperatureLog.logValue} ${temperatureLog.logUnit ?? ""}`
          : "-",
        humidity: humidityLog
          ? `${humidityLog.logValue} ${humidityLog.logUnit ?? ""}`
          : "-",
        co2: co2Log
          ? `${co2Log.logValue} ${co2Log.logUnit ?? ""}`
          : "-",
      };
    });
  }, [equipments, latestLogs, getEnumValueByEnumNameAndCode, t]);

  /* 2️⃣ index만 state */
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const [paused, setPaused] = useState(false);

  /* 3️⃣ selectedId 변경 시 해당 카드로 고정, 없으면 범위 보정 */
  useEffect(() => {
    if (selectedId !== null && selectedId !== undefined) {
      const targetIndex = cards.findIndex((card) => card.id === selectedId);
      if (targetIndex !== -1) {
        setIndex(targetIndex);
        return;
      }
    }

    // selectedId가 없거나 찾지 못한 경우 인덱스 범위 초과 방지
    if (index >= cards.length) {
      setIndex(0);
    }
  }, [selectedId, cards]);

  /* 4️⃣ 롤링 타이머 (selectedId가 유효한 경우 자동 롤링 비활성화) */
  useEffect(() => {
    // selectedId가 존재하고 actual card에 포함되어 있으면 롤링 금지
    const isSelectedMode = selectedId !== null && selectedId !== undefined && cards.some(c => c.id === selectedId);

    if (!autoRoll || isSelectedMode || paused || cards.length <= 1) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % cards.length);
    }, DELAY_TIME);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [cards, autoRoll, paused, selectedId]);

  /* 5️⃣ hover 이벤트 */
  const handleMouseEnter = () => {
    setPaused(true);
  };

  const handleMouseLeave = () => {
    setPaused(false);
    
    // selectedId로 선택되어 있는 모드라면 마우스가 나가도 다음 카드로 넘기지 않음
    const isSelectedMode = selectedId !== null && selectedId !== undefined && cards.some(c => c.id === selectedId);
    if (cards.length <= 1 || isSelectedMode) return;

    // 즉시 다음 카드로 이동
    setIndex((prev) => (prev + 1) % cards.length);
  };

  const current = cards[index] || EMPTY_CARD;

  const goEquipMove = () => {
    if (!current?.id) return;
    navigate(`/equipment/${current.id}`);
  };

  const currentStatusOrCommunicationIssueColor = getEquipmentStatusColorByStatusOrCommunicationIssue(current.status, (isViewCommError || role === ROLE.SA.code) && current.hasCommunicationIssue);

  return (
    <aside
      className={`${styles.card} ${styles.cardRoll} ${styles.slideInLeft}`}
      data-theme={mode === 'light' ? 'white' : 'dark'}
      aria-live="polite"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.row}>
        <span
          className={styles.pill}
          style={{
            color: currentStatusOrCommunicationIssueColor?.color,
            backgroundColor: currentStatusOrCommunicationIssueColor?.bg2,
            fontWeight: "bold",
            cursor: 'pointer',
          }}
          onClick={goEquipMove}
        >
          {current.name}
        </span>
      </div>

      {(current.imgFiles && current.imgFiles !== '-' && current.imgFiles.length > 0) ? (
        <img
          src={`${window.RUNTIME_CONFIG.REACT_APP_API_URL}/api/download/${current.imgFiles[0].id}`}
          className={styles.thumb}
          alt="img"
          style={{ width: "400px", cursor: 'pointer' }}
          onClick={goEquipMove}
        />
      ) : (
        <img
          src={`${process.env.PUBLIC_URL}/img/sample.png`}
          className={`${styles.thumb} ${styles.ghost}`}
          alt="img"
          style={{ width: "400px", cursor: 'pointer' }}
          onClick={goEquipMove}
        />
      )}

      <div className={styles.meta} data-theme={mode === 'light' ? 'white' : 'dark'}>
        {t('message.common.text.select.equipment.realtime.info.display')}
      </div>

      <div className={styles.row}>
        <div className={styles.pill}>
          {`${t('message.common.subtitle.equipment.level3')}: `}
          {current.equipmentCtgr?.name}
        </div>

        <div
          className={styles.pill}
          style={{
            color: getEquipmentStatusColorByStatus(current.status)?.color,
            background: getEquipmentStatusColorByStatus(current.status)?.bg2,
          }}
        >
          {`${t('message.common.subtitle.status')}: `}
          {current.statusTitle}
        </div>

        <div
          className={styles.pill}
          style={{
            color: "#fff",
            background:
              current.equipOpStatus?.toUpperCase() === 'ACTIVE'
                ? "#2672f7"
                : "#777",
          }}
        >
          {`${t('message.common.subtitle.operation.info')}: `}
          {current.equipOpStatusTitle}
        </div>

        {(role === ROLE.SA.code) && 
          <div
            className={styles.pill}
            style={{
              color: "#fff",
              background:
                current.hasCommunicationIssue
                  ? "#777"
                  : "#548235",
            }}
          >
            {`${t('message.common.subtitle.communication')}: `}
            {current.hasCommunicationIssue ? t('message.common.text.issue') : t('message.common.normal')}
          </div>
        }

        {current.lowestDiagnosisRank &&
          <div className={styles.pill} id="equipGrade" 
            style={{
              color: current.lowestDiagnosisRank === "C" ? "#000" : "#fff",
              background: equipmentISOStatus.find(iso => iso.code === current.lowestDiagnosisRank)?.color3
            }}
          >
            {`${t('message.common.subtitle.iso')}${t('message.common.subtitle.grade')}: `}
            {current.isoGrade.code}
          </div>
        }
        {process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' && current.temperature !== '-' &&
          <div className={styles.pill} id="equipTemp">
            {`${t('message.common.subtitle.temperature')}: `}
            {current.temperature}
          </div>
        }
        {current.humidity !== '-' &&
          <div className={styles.pill} id="equipHum">
            {`${t('message.common.subtitle.humidity')}: `}
            {current.humidity}
          </div>
        }
        {current.co2 !== '-' &&
          <div className={styles.pill} id="equipCo2">
            {`CO2: `}
            {current.co2}
          </div>
        }
      </div>

      {current.id ? (
        <DashboardEquipmentDurationGraphs equipmentId={current.id} height={170} />
      ) : null}
    </aside>
  );
}

export default memo(RightAsideCard);